import { z } from 'zod';
import type { ConsumableSelection } from './consumable.model';

export enum QuantityMode {
  FIXED = 'fixed',
  SCALES = 'scales',
}

export enum QuantityType {
  UNIT = 'unit',
  HOURLY = 'hourly',
  CUBIC_METER = 'cubic_meter',
  SQUARE_METER = 'square_meter',
  LITRE = 'litre',
}

/** Config for each quantity type; backend sends the id, use this to get labels/suffix. */
export const QuantityTypeLabels: Record<
  QuantityType,
  {
    label_singular: string;
    label_plural: string;
    suffix: string;
    long_label: string;
  }
> = {
  [QuantityType.UNIT]: {
    label_singular: 'unit',
    label_plural: 'units',
    suffix: 'pcs',
    long_label: 'Per Unit',
  },
  [QuantityType.HOURLY]: {
    label_singular: 'hour',
    label_plural: 'hours',
    suffix: 'hr',
    long_label: 'Per Hour',
  },
  [QuantityType.CUBIC_METER]: {
    label_singular: 'm³',
    label_plural: 'm³',
    suffix: 'm³',
    long_label: 'Per m³',
  },
  [QuantityType.SQUARE_METER]: {
    label_singular: 'm²',
    label_plural: 'm²',
    suffix: 'm²',
    long_label: 'Per m²',
  },
  [QuantityType.LITRE]: {
    label_singular: 'litre',
    label_plural: 'litres',
    suffix: 'L',
    long_label: 'Per Litre',
  },
};

/** Look up config for a validated quantity_type from the API. */
export function getQuantityTypeLabels(
  id: QuantityType,
): (typeof QuantityTypeLabels)[QuantityType] {
  return QuantityTypeLabels[id];
}

export function getQuantityTypeOptions(
  plural: boolean = false,
  labelType: 'short' | 'long' = 'short',
) {
  return Object.entries(QuantityTypeLabels).map(
    ([id, { label_singular, label_plural, long_label }]) => {
      return {
        value: id as QuantityType,
        label:
          labelType == 'short'
            ? plural
              ? label_plural
              : label_singular
            : long_label,
      };
    },
  );
}

// Schema for a single pricing information entry (per CAD object)
export const PricingInformationEntry = z.record(z.string(), z.string());
export type PricingInformationEntry = z.infer<typeof PricingInformationEntry>;

// Schema for all pricing parameters (keyed by cad_object_id)
// Format: { "cad_object_id_1": { param1: value, ... }, "cad_object_id_2": { ... }, ... }
export const PricingParameters = z.record(
  z.string(),
  // Pricing parameter values originate from JSON fields and may be booleans
  // or numbers (for example, `cosmetic_surface_required: true`). The editor
  // uses its existing string-or-null representation, so normalize scalar
  // values at the API boundary instead of rejecting otherwise valid data.
  z.record(z.string(), z.coerce.string().nullable()),
);
export type PricingParameters = z.infer<typeof PricingParameters>;

// Sentinel value used in the material dropdown to represent "Inherit from parent"
export const INHERIT_MATERIAL_ID = -1;
export const INHERIT_MATERIAL_LABEL = 'Inherit from Parent';

// Definition of a quote item in a blueprint or quote item data editor, when not yet saved to the backend
export interface QuoteItemData {
  persistent_id: string;
  name: string;
  cad_object_ids: number[];
  cad_object_names?: string[];
  drawing_file_id?: number | null;
  drawing_file_name?: string | null;
  capex_equipment_ids?: number[];
  consumables?: ConsumableSelection[];
  quantity?: string;
  unit_cost?: string;
  batch_size?: number | null;
  markup_percentage?: string;
  quantity_type?: QuantityType;
  material_id?: number | null;
  inherit_material?: boolean; // Whether this item inherits material from its nearest ancestor
  /** Unmatched AI material name, shown as a selection hint in the editor. */
  suggested_material_name?: string;
  /** Unmatched AI pricing-model name, shown as a selection hint in the editor. */
  suggested_pricing_model_name?: string;
  tax_code_id?: number | null; // Tax Data for BlueprintItems
  tax_percentage?: string | null; // Tax Data for quote revision items
  blueprint_id?: string; // ID of the blueprint this item references
  pricing_model_id?: number; // Optional pricing model ID to use for calculating prices
  pricing_parameters?: PricingParameters | null; // Custom pricing parameters keyed by CAD object ID
  parent_id: string | null; // null = root level, otherwise the persistent_id of the parent
  sibling_order: number; // Used for ordering siblings (sparse ordering, default spacing 100)
  /** Whether this item was loaded from a blueprint or created in the editor this session. */
  source?: 'loaded' | 'created';
}

const POSITIVE_DECIMAL_PATTERN = /^(?:0|[1-9]\d*)(?:\.\d{1,6})?$/;
const HH_MM_PATTERN = /^(\d+):([0-5]\d)$/;
const HH_MM_SS_PATTERN = /^(\d+):([0-5]\d):([0-5]\d)$/;

function isPositiveDecimal(value: string): boolean {
  if (!POSITIVE_DECIMAL_PATTERN.test(value)) {
    return false;
  }
  return parseFloat(value) > 0;
}

function parseTimeDuration(value: string): [number, number, number] | null {
  const match = HH_MM_SS_PATTERN.exec(value) ?? HH_MM_PATTERN.exec(value);
  if (!match) {
    return null;
  }
  const [hours, mins, secs = 0] = match
    .slice(1)
    .map((part) => parseInt(part, 10));
  return [hours, mins, secs];
}

function isPositiveTimeDuration(value: string): boolean {
  const hhMmSsMatch = value.match(HH_MM_SS_PATTERN);
  if (hhMmSsMatch) {
    const hours = parseInt(hhMmSsMatch[1], 10);
    const mins = parseInt(hhMmSsMatch[2], 10);
    const secs = parseInt(hhMmSsMatch[3], 10);
    return hours > 0 || mins > 0 || secs > 0;
  }

  const hhMmMatch = value.match(HH_MM_PATTERN);
  if (hhMmMatch) {
    const hours = parseInt(hhMmMatch[1], 10);
    const mins = parseInt(hhMmMatch[2], 10);
    return hours > 0 || mins > 0;
  }

  return false;
}

export function isValidQuantityInput(
  value: string,
  quantityType: QuantityType,
): boolean {
  const trimmed = value.trim();
  if (trimmed === '') {
    return true;
  }

  if (quantityType === QuantityType.HOURLY) {
    return isPositiveDecimal(trimmed) || isPositiveTimeDuration(trimmed);
  }

  return isPositiveDecimal(trimmed);
}

export function quantityToTime(quantity: number): [number, number, number] {
  const totalSeconds = Math.round(quantity * 3600);
  const hours = Math.floor(totalSeconds / 3600);
  const mins = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;
  return [hours, mins, secs];
}

export function timeToQuantity(
  hours: number,
  mins: number,
  secs: number,
): number {
  // Return quantity in hours, rounded to 6 decimal places
  return Math.round((hours + mins / 60 + secs / 3600) * 1_000_000) / 1_000_000;
}

/**
 * Convert editor quantity input into the decimal string expected by the API.
 * Well-formed, positive HH:MM[:SS] input on hourly items is converted to
 * decimal hours and valid decimals are passed through. Blank or malformed
 * input (anything `isValidQuantityInput` rejects) returns undefined so the
 * backend default quantity of 1 applies.
 */
export function quantityInputToDecimal(
  value: string | undefined,
  quantityType: QuantityType = QuantityType.UNIT,
): string | undefined {
  const trimmed = value?.trim();
  if (!trimmed || !isValidQuantityInput(trimmed, quantityType)) {
    return undefined;
  }
  if (quantityType === QuantityType.HOURLY && isPositiveTimeDuration(trimmed)) {
    const [hours, mins, secs] = parseTimeDuration(trimmed)!;
    return String(timeToQuantity(hours, mins, secs));
  }
  return trimmed;
}
